import Image from 'next/image'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import xss from 'xss'

import { Badge } from '@wartek-id/badge'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Button } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { Spinner } from '@wartek-id/spinner'
import { useToast } from '@wartek-id/toast'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { CheckboxGroup, Checkbox } from '@wartek-id/checkbox-v1'

import { ButtonPelaksanaanAppealRollback } from 'app/KinerjaPSPegawaiPage/components/ButtonPelaksanaanAppealRollback'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { Header } from 'app/KinerjaShared/components/Header'
import { usePelaksanaanPraktikKinerjaPS } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPraktikKinerjaPS'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'app/KinerjaPSPegawaiPage/components/Form'
import { nl2br, stripHTMLTags } from 'utils/text'
import { InternalServerError } from 'utils/customError'
import useToggleSdkContext from 'utils/useToogleSdk'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'

import type { TPraktikKinerjaUpayaTindakLanjutFormValue } from 'types/kinerja-ps-pegawai'
import type { SubmitHandler } from 'react-hook-form'

const DialogConfirmation = ({
  onBack,
  onConfirm,
  isSubmitting = false,
  ...dialogProps
}: Omit<DialogProps, 'children'> & {
  isSubmitting?: boolean
  onBack?: () => void
  onConfirm: () => void
}) => {
  const [checked, setChecked] = useState([])
  const isConfirmed = checked.filter((item) => Boolean(item)).length === 2

  return (
    <Dialog {...dialogProps}>
      <DialogOverlay />
      <DialogContent>
        <div className="p-6">
          <div className="mb-6">
            <Text variant="heading-md" className="mb-5">
              Saya menyatakan sudah:
            </Text>

            <CheckboxGroup
              name="upaya-tindak-lanjut-confirmation"
              onChange={setChecked}
              value={checked}
              className="flex flex-col gap-2"
            >
              <Checkbox value="menyelesaikan" labelPosition="center">
                <Text>Menyelesaikan Upaya Tindak Lanjut</Text>
              </Checkbox>
              <Checkbox value="mengetahui" labelPosition="center">
                <Text>
                  Mengetahui bahwa Refleksi Tindak Lanjut akan dilakukan setelah
                  ini
                </Text>
              </Checkbox>
            </CheckboxGroup>
          </div>

          <div className="flex gap-3">
            <Button size="lg" color="white" onClick={onBack}>
              Kembali
            </Button>
            <Button
              size="lg"
              color="blue"
              onClick={onConfirm}
              isLoading={isSubmitting}
              disabled={!isConfirmed || isSubmitting}
            >
              Lanjut kumpulkan
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const MSG_ERR_REQUIRED = 'Wajib diisi'

export default function PSPelaksanaanUpayaTindakLanjutPage() {
  const toast = useToast()
  const router = useRouter()
  const { trackEvent } = usePPSTrackEvent()

  const toggle = useToggleSdkContext()
  const appealRollbackEnabled =
    toggle?.['kinerja-ps-pegawai-appeal-rollback-enabled'] || false

  const form = useForm<TPraktikKinerjaUpayaTindakLanjutFormValue>({
    defaultValues: {
      realisasi: '',
    },
  })

  // Fetch "Pendampingan Sekolah" form data
  const {
    isLoading: isPendampinganSekolahLoading,
    formStatus: pendampinganSekolahFormStatus,
    parsedFormValues: parsedPendampinganSekolahValues,
  } = usePelaksanaanPraktikKinerjaPS<any>({
    formName: 'pendampingan_sekolah',
  })

  // Fetch "Upaya Tindak Lanjut" form data
  const {
    isLoading,
    isSuccess,
    isViewOnly,
    isAppeal,
    isLocked,
    parsedFormValues,
    submitFormValues,
    submitStatus,
    psSummaryHookResult,
  } = usePelaksanaanPraktikKinerjaPS<TPraktikKinerjaUpayaTindakLanjutFormValue>(
    {
      formName: 'upaya_tindak_lanjut',
    }
  )

  const isSubmitDisabled = submitStatus === 'loading' || !form.formState.isDirty

  useEffect(() => {
    if (isSuccess) {
      Object.entries(parsedFormValues).forEach(([name, value]: any) =>
        form.setValue(name, value, {
          shouldDirty: true,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedFormValues, isSuccess])

  useEffect(() => {
    if (
      psSummaryHookResult.summaryFetchStatus === 'success' &&
      !isLoading &&
      !isSuccess
    ) {
      throw new InternalServerError(
        'API_PATH',
        new Error('Failed to fetch summary or form values')
      )
    }

    if (isSuccess) {
      if (!psSummaryHookResult.isPelaksanaanState) {
        router.replace(PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH)
      } else if (isLocked) {
        router.replace(
          `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    isLocked,
    isSuccess,
    psSummaryHookResult.isPelaksanaanState,
    psSummaryHookResult.summaryFetchStatus,
  ])

  const [isDialogConfirmationOpen, setIsDialogConfirmationOpen] =
    useState(false)
  const onSubmit: SubmitHandler<
    TPraktikKinerjaUpayaTindakLanjutFormValue
  > = () => {
    setIsDialogConfirmationOpen(true)
  }

  const handleSubmitFormValues = async (saveAsDraft = true) => {
    try {
      await submitFormValues({ values: form.getValues(), saveAsDraft })

      trackEvent('pelaksanaan pegawai', 'submit form D')

      toast({
        message: 'Berhasil simpan dokumen!',
      })

      if (window.history.state.idx <= 1) {
        router.push(
          `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
        )
      } else {
        router.back()
      }
    } catch (e) {
      console.error(e)
      toast({
        message: 'Gagal simpan dokumen. Coba lagi.',
      })
    } finally {
      setIsDialogConfirmationOpen(false)
    }
  }

  return (
    <div className="px-3 mx-auto max-w-7xl py-9">
      <Header
        title={
          <div className="flex items-center gap-x-3">
            Upaya Tindak Lanjut
            {isViewOnly && !isAppeal && (
              <Badge variant="success">Selesai</Badge>
            )}
            {isAppeal && (
              <Badge variant="neutral">Perubahan sedang diajukan</Badge>
            )}
          </div>
        }
        className="mb-6"
      />

      {isLoading && (
        <div
          data-testid="loading-spinner-upaya-tindak-lanjut"
          className="flex justify-center w-full p-6"
        >
          <Spinner />
        </div>
      )}

      {isSuccess && (
        <>
          {isViewOnly || isAppeal ? (
            <div className="max-w-4xl px-3 mx-auto py-9">
              <SimpleCard className="flex-1 !rounded mb-24">
                {isSuccess && (
                  <>
                    <div className="flex flex-col gap-1 p-6 border-b rounded-t bg-blue-5 border-b-surface-subdued">
                      <Text variant="heading-lg">
                        Berikut isi catatan yang sudah dikumpulkan:
                      </Text>
                    </div>

                    <div className="p-6 space-y-6">
                      <div className="space-y-2 bg-white rounded-b">
                        <div className="flex items-center gap-x-3">
                          <div className="flex items-center justify-center w-6 h-6 text-white rounded-full bg-blue-70">
                            <Text color="inverse" variant="body-sm-bold">
                              1
                            </Text>
                          </div>
                          <Text variant="action">
                            Tiga upaya yang sudah Anda lakukan:
                          </Text>
                        </div>
                        <div className="pl-9">
                          <Text
                            color="subdued"
                            dangerouslySetInnerHTML={{
                              __html: xss(
                                nl2br(
                                  stripHTMLTags(
                                    parsedFormValues?.realisasi ?? '-'
                                  )
                                )
                              ),
                            }}
                          />
                        </div>
                      </div>

                      {appealRollbackEnabled && (
                        <div className="flex justify-end">
                          <ButtonPelaksanaanAppealRollback formName="upaya_tindak_lanjut" />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </SimpleCard>
            </div>
          ) : (
            <>
              <div className="flex gap-6">
                <SimpleCard className="p-5 space-y-6 h-fit w-72">
                  <div className="flex items-center gap-4">
                    <Image
                      src="/illustrations/img-document-penci-fill.png"
                      width={48}
                      height={48}
                      alt=""
                    />

                    <Text variant="heading-lg">Panduan</Text>
                  </div>

                  <ul className="pl-6 list-disc">
                    <li>
                      <Text>
                        Upaya idealnya dilakukan selama kurang lebih 1 bulan.
                      </Text>
                    </li>
                    <li>
                      <Text>
                        Jika sudah melakukan upaya, isi realisasi upaya yang
                        sudah Anda lakukan.
                      </Text>
                    </li>
                  </ul>
                </SimpleCard>

                <SimpleCard className="flex-1 !rounded mb-24">
                  {isSuccess && (
                    <>
                      <div className="flex flex-col gap-1 p-6 border-b rounded-t bg-blue-5 border-b-surface-subdued">
                        <Text variant="heading-lg">
                          Isi realisasi upaya yang sudah Anda lakukan (kurang
                          lebih 1 bulan)
                        </Text>
                        <Text>
                          Pengisian ini menjadi rujukan Anda untuk mengisi
                          Refleksi Tindak Lanjut dengan lebih matang.
                        </Text>
                      </div>
                      <Form {...form}>
                        <form
                          onSubmit={form.handleSubmit(onSubmit)}
                          className="flex flex-col gap-6 p-6"
                        >
                          <SimpleCard className="flex-1 !rounded">
                            <div className="p-4 space-y-2">
                              <div>
                                <Text>Target Anda</Text>
                                <Text variant="action">
                                  Rencana Anda di tahap Pendampingan:
                                </Text>
                              </div>

                              {isPendampinganSekolahLoading && (
                                <Shimmer className="!w-full" />
                              )}

                              {pendampinganSekolahFormStatus === 'success' && (
                                <Text
                                  color="subdued"
                                  dangerouslySetInnerHTML={{
                                    __html: xss(
                                      nl2br(
                                        stripHTMLTags(
                                          parsedPendampinganSekolahValues?.upaya ??
                                            '-'
                                        )
                                      )
                                    ),
                                  }}
                                />
                              )}
                            </div>
                          </SimpleCard>

                          <FormField
                            control={form.control}
                            name="realisasi"
                            rules={{
                              required: MSG_ERR_REQUIRED,
                            }}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  <Text variant="body" className="mb-1">
                                    Realisasi Anda
                                  </Text>
                                  <Text variant="body-bold" className="mb-1">
                                    Berdasarkan rencana di atas, sebutkan
                                    minimal 3 upaya yang sudah Anda lakukan:
                                  </Text>
                                </FormLabel>

                                <FormControl>
                                  <Textarea
                                    placeholder="Ketik di sini"
                                    aria-label="Berdasarkan rencana di atas, sebutkan minimal 3
                            upaya yang sudah Anda lakukan:"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="fixed bottom-0 left-0 right-0 z-10 flex justify-end w-full gap-4 px-24 py-4 bg-white border-t border-subdued">
                            <Button
                              color={isSubmitDisabled ? 'blue' : 'white'}
                              size="md"
                              className={clsx(
                                '!px-6',
                                isSubmitDisabled &&
                                  '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
                              )}
                              onClick={() => handleSubmitFormValues(true)}
                              isLoading={submitStatus === 'loading'}
                              disabled={isSubmitDisabled}
                            >
                              Simpan draf
                            </Button>
                            <Button
                              color="blue"
                              size="md"
                              className={clsx(
                                '!px-6',
                                isSubmitDisabled &&
                                  '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
                              )}
                              type="submit"
                              isLoading={submitStatus === 'loading'}
                              disabled={isSubmitDisabled}
                            >
                              Kumpulkan
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </>
                  )}
                </SimpleCard>
              </div>

              <DialogConfirmation
                isSubmitting={submitStatus === 'loading'}
                isOpen={isDialogConfirmationOpen}
                onConfirm={() => {
                  handleSubmitFormValues(false).finally(() => {
                    setIsDialogConfirmationOpen(false)
                  })
                }}
                onBack={() => setIsDialogConfirmationOpen(false)}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
